"use strict";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createNativeBridge } from "./bridge_native.js";
import { getOrCreateWebBridge } from "./bridge_web.js";
import { jsx as _jsx } from "react/jsx-runtime";
export function useBridge(options) {
  const ref = useRef(null);
  // Lazy init via useState (not useRef + lazy-set) so StrictMode's effect
  // double-invoke doesn't dispose the bridge mid-lifetime.
  const [bridge] = useState(() => createNativeBridge(ref, options));
  return {
    ref,
    bridge
  };
}
const WebBridgeContext = /*#__PURE__*/createContext(null);
export function WebBridgeProvider({
  logger,
  children
}) {
  // No dispose-on-unmount: useEffect cleanup would fire after StrictMode's
  // first mount and leave the (lazy-init-once) bridge disposed. Provider
  // lives at the app root for the page's lifetime anyway.
  const [bridge] = useState(() => getOrCreateWebBridge({
    logger
  }));
  return /*#__PURE__*/_jsx(WebBridgeContext.Provider, {
    value: bridge,
    children: children
  });
}
export function useWebBridge() {
  const ctxBridge = useContext(WebBridgeContext);
  if (ctxBridge) {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    return ctxBridge;
  } else {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    return getOrCreateWebBridge();
  }
}
export function useAppBridge(handlers) {
  const bridge = useWebBridge();
  useExpose(bridge, '__navigation/proposeVisit', handlers.proposeVisit);
  const proposeVisitRef = useRef(handlers.proposeVisit);
  useEffect(() => {
    proposeVisitRef.current = handlers.proposeVisit;
  });
  useEffect(() => {
    const legacyWindow = window;
    if (!legacyWindow.pca) {
      legacyWindow.pca = {};
    }
    legacyWindow.pca.navigate = to => {
      void proposeVisitRef.current({
        to
      });
    };
  }, []);
  return bridge;
}
export function useBridgeNavigation(bridge) {
  return useBridgeFunction(bridge, '__navigation/proposeVisit');
}
export function useExpose(bridge, name, handler) {
  // Stash the latest handler in a ref so the wrapper below has stable
  // identity — no re-expose churn when the consumer passes an inline handler
  // that changes reference every render. Ref update happens in commit phase
  // (not render) to satisfy the react-compiler/react-compiler lint rule.
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    const wrapper = args => handlerRef.current(args);
    bridge.expose(name, wrapper);
    return () => {
      bridge.unexpose(name);
    };
  }, [bridge, name]);
}
export function useBridgeFunction(bridge, name, options) {
  const subscribe = useCallback(cb => bridge.onRemoteAction(name, () => cb()), [bridge, name]);
  const getSnapshot = useCallback(() => bridge.hasRemoteAction(name), [bridge, name]);
  const isAvailable = useSyncExternalStore(subscribe, getSnapshot);
  const timeoutMs = options?.timeoutMs;
  const fn = useMemo(() => args => bridge.call(name, args, {
    timeoutMs
  }), [bridge, name, timeoutMs]);
  if (isAvailable) {
    return {
      isAvailable: true,
      fn
    };
  } else {
    return {
      isAvailable: false,
      fn: undefined
    };
  }
}
export function useBridgePublish(bridge, name, value) {
  // publish() dedups by shallow equality, so this effect is safe to re-run
  // on every value change without spamming wire messages.
  useEffect(() => {
    bridge.publish(name, value);
  }, [bridge, name, value]);
  useEffect(() => {
    return () => {
      bridge.unpublish(name);
    };
  }, [bridge, name]);
}
export function useBridgeValue(bridge, name) {
  const subscribe = useCallback(cb => bridge.onRemoteValue(name, () => cb()), [bridge, name]);
  const getSnapshot = useCallback(() => bridge.getRemoteValue(name), [bridge, name]);
  const value = useSyncExternalStore(subscribe, getSnapshot);
  return {
    value,
    isAvailable: value !== undefined
  };
}
export function useHasCapability(bridge, name) {
  const {
    value
  } = useBridgeValue(bridge, 'capabilities');
  return value?.includes(name) ?? false;
}
//# sourceMappingURL=hooks.js.map